/**
 * Wording for the tender side panel, kept pure so it is testable without i18n or a DOM.
 *
 * Three things live here: dates as people read them (a date without a time is „ganztägig", never the
 * „01:00" the UTC midnight turns into — tri-server docs/tender-dates.md), the Verlauf in plain words
 * („Du hast …", names instead of ids, no engine vocabulary such as `TASK_COMPLETED`), and the short
 * excerpt of the conversation the open task is about.
 */
import type { TenderActivityEntry } from "../../TenderSearchProfileAPI";

const ZONE = "Europe/Berlin";

/** Ein Punkt ans Ende — außer der Satz endet schon auf einem Satzzeichen („Passt die Gliederung?."). */
export const endSentence = (text: string): string => (/[.!?…]["“”»]?$/.test(text.trim()) ? text.trim() : `${text.trim()}.`);

/**
 * Der Browser meldet eine nicht erreichbare Verbindung als `TypeError` mit „Failed to fetch"
 * (Chrome), „NetworkError …" (Firefox) oder „Load failed" (Safari) — roh und englisch stand das
 * im Panel (Prod 22.10.2026).
 */
export const isNetworkError = (err: unknown): boolean =>
  /failed to fetch|networkerror|load failed|network request failed/i.test(String((err as any)?.message ?? err ?? ""));

/** A date the server stored without a time: UTC midnight. */
export const isDateOnly = (iso: string): boolean => {
  const date = new Date(iso);
  return date.getUTCHours() === 0 && date.getUTCMinutes() === 0 && date.getUTCSeconds() === 0;
};

/** „22. Oktober 2026" and „12:00" — or no time for a date-only value, on its UTC calendar day. */
export const describeDateTime = (iso: string): { date: string; time: string | null } => {
  const value = new Date(iso);
  if (isDateOnly(iso)) {
    return {
      date: value.toLocaleDateString("de-DE", { timeZone: "UTC", day: "numeric", month: "long", year: "numeric" }),
      time: null,
    };
  }
  return {
    date: value.toLocaleDateString("de-DE", { timeZone: ZONE, day: "numeric", month: "long", year: "numeric" }),
    time: value.toLocaleTimeString("de-DE", { timeZone: ZONE, hour: "2-digit", minute: "2-digit" }),
  };
};

/** „vor 5 Min.", „vor 2 Std.", „gestern, 11:05", „09.10.2026" — for the excerpt and the chat cards. */
export const relativeTime = (iso: string, now: Date): string => {
  const at = new Date(iso);
  const minutes = Math.round((now.getTime() - at.getTime()) / 60_000);
  if (minutes < 1) return "gerade eben";
  if (minutes < 60) return `vor ${minutes} Min.`;
  if (minutes < 12 * 60) return `vor ${Math.round(minutes / 60)} Std.`;
  const day = (date: Date) => date.toLocaleDateString("de-DE", { timeZone: ZONE });
  const time = at.toLocaleTimeString("de-DE", { timeZone: ZONE, hour: "2-digit", minute: "2-digit" });
  if (day(at) === day(now)) return `heute, ${time}`;
  if (day(at) === day(new Date(now.getTime() - 86_400_000))) return `gestern, ${time}`;
  return at.toLocaleDateString("de-DE", { timeZone: ZONE, day: "2-digit", month: "2-digit", year: "numeric" });
};

/**
 * A few plain sentences out of a bot message: no markdown, no payload blocks, no tables. The full
 * message is one click away in the chat tab — this only says what the question is about.
 *
 * No HTML either: a process step's message carries its progress lines as
 * `<div class="progress">…</div>` (the chat draws them as steps), and the excerpt showed them raw —
 * „<div class="progress">Präsentation wird erstellt...</div …" above „Konzept ergänzen" (Prod-Test
 * 24.10.2026).
 */
export const plainExcerpt = (markdown: string, max = 280): string => {
  const text = markdown
    .replace(/<div\s+[^>]*class\s*=\s*["']progress["'][^>]*>[\s\S]*?<\/div>/gi, " ")
    .replace(/<\/?[a-z][^>]*>/gi, " ")
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/^\s*\|.*\|\s*$/gm, " ")
    .replace(/!\[[^\]]*\]\([^)]*\)/g, " ")
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/[*_`>~]/g, "")
    .replace(/^\s*[-•]\s+/gm, "")
    .replace(/\s+/g, " ")
    .trim();
  if (text.length <= max) return text;
  const cut = text.slice(0, max);
  return `${cut.slice(0, Math.max(cut.lastIndexOf(" "), max - 20)).trim()} …`;
};

/**
 * The part of a bot message the open task is about. A written concept ends with „### Hinweise zum
 * Entwurf" (chapters, open placeholders, the files) — that is what „Konzept ergänzen" asks about,
 * not the first sentences of chapter 1. Every other message is taken whole.
 */
export const taskExcerptSource = (markdown: string): string => {
  const notes = /^#{2,4}\s*Hinweise zum Entwurf\s*$/m.exec(markdown);
  return notes ? markdown.slice(notes.index + notes[0].length) : markdown;
};

export type HistoryCategory = "decision" | "dates" | "other";

export type HistoryLine = {
  at: string;
  /** Who: „Du", a name, or null for the system. */
  who: string | null;
  initials: string;
  isSystem: boolean;
  text: string;
  /** A second, quieter line — the reason of a dismissal, the department of a handover. */
  detail: string | null;
  category: HistoryCategory;
};

const initialsOf = (name: string): string =>
  name
    .split(/[\s.\-_+@]/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");

const str = (value: unknown): string | null => (typeof value === "string" && value.trim() ? value.trim() : null);

/**
 * One Verlauf entry in plain words. `me` is the viewer's e-mail, `people` maps e-mails to names; an
 * actor nobody can name is shown by the local part of the address, never by an id.
 */
export const describeHistoryEntry = (
  entry: TenderActivityEntry,
  me: string | null,
  people: Record<string, string>
): HistoryLine => {
  const actor = entry.actor;
  const isMe = !!actor && !!me && actor.toLowerCase() === me.toLowerCase();
  const name = actor
    ? (isMe ? "Du" : (entry.actorName ?? people[actor] ?? actor.replace(/@.*$/, "")))
    : null;
  const subject = name ?? "TRI";
  const has = isMe ? "hast" : "hat";
  const detail = entry.detail ?? {};
  const node = str(detail.nodeName);

  let text: string;
  let line: string | null = null;
  let category: HistoryCategory = "other";

  switch (entry.kind) {
    case "owner-assigned": {
      const owner = str(detail.ownerUserId);
      category = "decision";
      text =
        owner && actor && owner.toLowerCase() === actor.toLowerCase()
          ? `${subject} ${has} die Ausschreibung übernommen.`
          : `${subject} ${has} die Ausschreibung ${owner ? `${people[owner] ?? owner.replace(/@.*$/, "")} ` : ""}zugewiesen.`;
      break;
    }
    case "owner-released":
      category = "decision";
      text = `${subject} ${has} die Ausschreibung freigegeben.`;
      break;
    case "handover":
      category = "decision";
      text = `${subject} ${has} die Ausschreibung an ${str(detail.roleName) ?? "eine andere Abteilung"} weitergegeben.`;
      line = str(detail.previousRoleName) ? `Vorher: ${detail.previousRoleName}` : null;
      break;
    case "triage-dismissed":
      category = "decision";
      text = `${subject} ${has} die Ausschreibung verworfen.`;
      line = str(detail.reason) ? `Grund: ${detail.reason}` : null;
      break;
    case "triage-restored":
      category = "decision";
      text = `${subject} ${has} die Ausschreibung zurückgeholt.`;
      break;
    case "triage-started":
      category = "decision";
      text = `${subject} ${has} den Prozess gestartet.`;
      break;
    case "phase-changed":
      category = "decision";
      text = `Status: ${str(detail.phaseLabel) ?? str(detail.phase) ?? "geändert"}${
        str(detail.outcomeLabel) ? ` (${detail.outcomeLabel})` : ""
      }.`;
      break;
    case "date-added":
      category = "dates";
      text = `${subject} ${has} einen Termin hinzugefügt: ${str(detail.label) ?? "ohne Bezeichnung"}.`;
      break;
    case "date-removed":
      category = "dates";
      text = `${subject} ${has} einen Termin entfernt: ${str(detail.label) ?? "ohne Bezeichnung"}.`;
      break;
    case "existing-customer-confirmed":
      category = "decision";
      text = `${subject} ${has} bestätigt: ${str(detail.name) ?? "Auftraggeber"} ist Bestandskunde.`;
      break;
    case "existing-customer-rejected":
      category = "decision";
      text = `${subject} ${has} markiert: ${str(detail.name) ?? "Auftraggeber"} ist kein Kunde.`;
      break;
    case "existing-customer-reset":
      category = "decision";
      text = `${subject} ${has} die Bestandskunden-Einschätzung zurückgesetzt.`;
      break;
    case "INSTANCE_STARTED":
      text = "Prozess gestartet.";
      break;
    case "TASK_STARTED":
      // A human task waiting is worth a line; the engine's own service steps are not people.
      text = node ? endSentence(`Neue Aufgabe: ${node}`) : "Neue Aufgabe.";
      break;
    case "TASK_COMPLETED":
      if (actor) {
        category = "decision";
        text = node ? `${subject} ${has} „${node}“ erledigt.` : `${subject} ${has} eine Aufgabe erledigt.`;
      } else {
        text = node ? endSentence(`Erledigt: ${node}`) : "Ein Schritt ist erledigt.";
      }
      break;
    default:
      // A kind this portal does not know yet: the server's own German label, never the slug.
      text = entry.label;
  }

  return {
    at: entry.at,
    who: name,
    initials: name ? (isMe && me ? initialsOf(people[me] ?? me) : initialsOf(name)) || "?" : "TRI",
    isSystem: !actor,
    text,
    detail: line,
    category,
  };
};

/** „HEUTE, MO 19.10.2026", „GESTERN, SO 18.10.2026", „FR, 09.10.2026" — newest day first. */
export const groupByDay = <T extends { at: string }>(
  lines: T[],
  now: Date
): { label: string; lines: T[] }[] => {
  const key = (date: Date) => date.toLocaleDateString("de-DE", { timeZone: ZONE, day: "2-digit", month: "2-digit", year: "numeric" });
  const weekday = (date: Date) =>
    date.toLocaleDateString("de-DE", { timeZone: ZONE, weekday: "short" }).replace(/\.$/, "").toUpperCase();
  const today = key(now);
  const yesterday = key(new Date(now.getTime() - 86_400_000));
  const groups: { label: string; lines: T[] }[] = [];
  const sorted = [...lines].sort((a, b) => Date.parse(b.at) - Date.parse(a.at));
  for (const line of sorted) {
    const at = new Date(line.at);
    const day = key(at);
    const label =
      day === today
        ? `HEUTE, ${weekday(at)} ${day}`
        : day === yesterday
          ? `GESTERN, ${weekday(at)} ${day}`
          : `${weekday(at)}, ${day}`;
    const last = groups[groups.length - 1];
    if (last?.label === label) last.lines.push(line);
    else groups.push({ label, lines: [line] });
  }
  return groups;
};
